import { selectHotelWebsite } from 'accessors'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { getCleanUrl } from 'utils/common'

import styles from './EditTextDialog.module.scss'
import { optOutText } from './utils/textContentStrings'

const customLinkUrlRegex = /^(http|https)?:\/\/\S+/i
const previewEncodedShortLinkId = 'hsa88'
const fallbackInviteShortLinkDomain = 'deals.directful.com'

// Tags are stored as @[display](__id__), but the display name depends on where the
// tag was inserted from (the composer writes firstName while the backend format
// writes guestName for the same __guestName__ id). Always resolve by id.
const taggedPlaceholderRegex = /@\[([^\]]*)\]\(([^)]*)\)/g
const barePlaceholderRegex =
  /\b(guestLastName|guestName|firstName|lastName|hotelName|promoName|checkinDate|checkoutDate|inviteLink)\b/g

// Wraps the resolved invite link so it can be split back out and styled without
// matching unrelated occurrences of the same URL inside the message.
const linkBoundary = '\u0000'

const chainSubdomainByParentChainCode = {
  bestwestern: 'bestwestern',
  choice: 'choicehotels',
  hilton: 'hilton',
  hyatt: 'hyatt',
  ihg: 'ihg',
  marriott: 'marriott',
  radisson: 'radissonhotels',
  sonesta: 'sonesta',
  wyndham: 'wyndhamhotels'
}

export function getInviteShortLinkPreview(subsidiaryShortLinkToken, parentChainCode) {
  const token = subsidiaryShortLinkToken?.trim()
  if (!token) {
    return `on.directful/${previewEncodedShortLinkId}`
  }

  const subdomain = chainSubdomainByParentChainCode[parentChainCode]
  const domain = subdomain
    ? `${subdomain}.directful.com`
    : fallbackInviteShortLinkDomain

  return `${domain}/${token}/${previewEncodedShortLinkId}`
}

export function resolvePreviewLinkUrl(
  customLink,
  hotelWebsiteUrl,
  inviteShortLinkPreview = null
) {
  const custom = customLink?.trim()
  if (custom && customLinkUrlRegex.test(custom)) {
    return custom
  }

  const inviteShortLink = inviteShortLinkPreview?.trim()
  if (inviteShortLink) {
    return inviteShortLink
  }

  const hotel = hotelWebsiteUrl?.trim()
  if (hotel) {
    return getCleanUrl(hotel) || hotel
  }

  return null
}

export function getPreviewFirstName(userName) {
  const [firstName] = `${userName || ''}`.trim().split(/\s+/).filter(Boolean)
  return firstName || 'Guest'
}

export function getPreviewLastName(userName) {
  const parts = `${userName || ''}`.trim().split(/\s+/).filter(Boolean)
  return parts.length > 1 ? parts[parts.length - 1] : ''
}

export function replacePreviewPlaceholders(text, context = {}) {
  if (!text) {
    return ''
  }

  const { userName, hotelName, promotionName, url } = context
  const firstName = getPreviewFirstName(userName)
  const lastName = getPreviewLastName(userName)

  const values = {
    guestName: firstName,
    firstName,
    guestLastName: lastName,
    lastName,
    hotelName: hotelName || '',
    promoName: promotionName || '',
    checkinDate: moment().add(1, 'day').format('MMMM D, YYYY'),
    checkoutDate: moment().add(3, 'day').format('MMMM D, YYYY'),
    inviteLink: url ? `${linkBoundary}${url}${linkBoundary}` : ''
  }

  return text
    .replace(taggedPlaceholderRegex, (match, display, id) => {
      const key = id.replace(/^_+|_+$/g, '')
      return key in values ? values[key] : display
    })
    .replace(barePlaceholderRegex, match => values[match])
    .replace(optOutText, '')
}

export function buildPreviewSegments(text, context = {}) {
  return replacePreviewPlaceholders(text, context)
    .split(linkBoundary)
    .map((part, index) => ({ text: part, isLink: index % 2 === 1 }))
    .filter(segment => segment.text !== '')
}

export const TextPreview = ({ children, header, className }) => {
  return (
    <div>
      {header && <div className={styles.previewHeader}>{header}</div>}
      <div className={[styles.previewDialog, className].filter(Boolean).join(' ')}>
        <div className={styles.previewInner}>
          <div className={styles.previewScroll}>{children}</div>
        </div>
      </div>
    </div>
  )
}

export const TextPreviewBubble = ({
  prologue = '',
  text,
  defaultData,
  hasOptOutText,
  hasLink,
  customLink,
  preferShortLink = false
}) => {
  const isDemoMode = useSelector(s => s.login.isDemoMode)
  const hotelWebsiteUrl = useSelector(selectHotelWebsite)
  const subsidiaryShortLinkToken = useSelector(
    s => s.hotelDetails.subsidiaryShortLinkToken
  )
  const parentChainCode = useSelector(s => s.hotelDetails.parentChainCode)

  if (!text) {
    return null
  }

  const inviteShortLinkPreview = preferShortLink
    ? getInviteShortLinkPreview(subsidiaryShortLinkToken, parentChainCode)
    : null
  const url = resolvePreviewLinkUrl(
    customLink,
    hotelWebsiteUrl,
    inviteShortLinkPreview
  )

  function replaceTags(text) {
    const previewContext = {
      userName: defaultData.userName,
      hotelName: isDemoMode ? 'The Luxury Resort & Spa' : defaultData.hotelName,
      promotionName: defaultData.promotionName,
      url
    }

    return buildPreviewSegments(text, previewContext).map((segment, i) =>
      segment.isLink ? (
        <span className={styles.bubbleLink} key={`t-${i}`}>
          {segment.text}
        </span>
      ) : (
        <span key={`t-${i}`}>{segment.text}</span>
      )
    )
  }
  return (
    <div className={styles.textBubble}>
      <span style={{ opacity: 0.4 }}>{replaceTags(prologue)}</span>
      {replaceTags(text)}
      {hasLink && url && <span className={styles.bubbleLink}>{url}</span>}
      {hasOptOutText && '\n' + optOutText}
      <span className={styles.bubbleTail}></span>
    </div>
  )
}
